import { style } from "@angular/animations";
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from "@angular/core";
import { DomSanitizer, SafeResourceUrl } from "@angular/platform-browser";
import { environment } from "src/environments/environment";

@Component({
  selector: "app-audio-player",
  templateUrl: "./audio-player.component.html",
  styleUrls: ["./audio-player.component.scss"],
  standalone: false,
})
export class AudioPlayer implements OnInit, AfterViewInit {
  @Input() duration!: number;
  private _url: string = "";
  @Input() set url(value: string) {
    this._url = value ?? "";
    this.updateSafeUrl();
  }
  get url(): string {
    return this._url;
  }
  @Input() styles?: { [key: string]: string };
  @Output() playRequest = new EventEmitter<AudioPlayer>();
  progressBarValue: number = 0.0;
  timeCountValue: number = 0.0;
  audioRef: any;
  inPause = false;
  started = false;
  private audio!: HTMLMediaElement;
  @ViewChild("audioElement", { static: false }) public _audioRef!: ElementRef;
  safeUrl: SafeResourceUrl | null = null;
  private normalizedUrl: string = "";
  ngOnInit() {
    this.timeCountValue = this.duration + 1000;
  }
  ngAfterViewInit() {
    this.audio = this._audioRef?.nativeElement;
    if (this.audio) {
      this.audio.autoplay = false;
      this.audio.volume = 1;
      this.reloadAudioSource();
    }
  }
  progressbarWitdh(progressBarValue: any, duration: any) {
    const progress = (progressBarValue / (duration / 1000)) * 100;
    return progress <= 100 ? progress : 100;
  }

  onTimeUpdate(event: any) {
    const audioElement: HTMLAudioElement = event.target;
    this.progressBarValue = audioElement.currentTime;
    this.timeCountValue = this.duration - audioElement.currentTime * 1000;
  }

  formatTime(time: any) {
    const timeVal = (time / 100).toFixed(2);
    const val =
      Number(timeVal) >= 0.6
        ? "1:00"
        : Number(timeVal) <= 0
        ? "0:00"
        : timeVal.replace(".", ":");
    return val;
  }
  pause(): void {
    if (this.audio) {
      this.audio.pause();
    }
  }
  stopPlayback(): void {
    if (this.audio) {
      this.audio.pause();
      this.audio.currentTime = 0;
    }
    this.progressBarValue = 0;
    this.timeCountValue = this.duration + 1000;
    this.started = false;
  }
  play() {
    if (!this.audio) {
      return;
    }
    this.playRequest.emit(this);
    this.started = true;
    this.audio.play().catch((error) => {
      console.debug("Audio playback failed, reloading source", error);
      this.reloadAudioSource(true);
    });
  }
  get paused(): boolean {
    if (this.audio) {
      return this.audio.paused;
    } else {
      return true;
    }
  }
  constructor(private sanitizer: DomSanitizer) {}

  private updateSafeUrl() {
    this.normalizedUrl = this.normalizeUrl(this._url);
    this.safeUrl = this.normalizedUrl
      ? this.sanitizer.bypassSecurityTrustResourceUrl(this.normalizedUrl)
      : null;
    if (this.audio) {
      this.reloadAudioSource();
    }
  }

  private normalizeUrl(value: string): string {
    if (!value) {
      return "";
    }
    const trimmedValue = value.trim();
    if (!trimmedValue) {
      return "";
    }
    if (
      trimmedValue.startsWith("data:") ||
      trimmedValue.startsWith("blob:") ||
      trimmedValue.startsWith("capacitor://") ||
      trimmedValue.startsWith("file://")
    ) {
      return trimmedValue;
    }
    if (trimmedValue.startsWith("http://")) {
      return trimmedValue.replace("http://", "https://");
    }
    if (trimmedValue.startsWith("https://")) {
      return trimmedValue;
    }
    const base = environment.imagePath?.replace(/\/$/, "") ?? "";
    const sanitizedPath = trimmedValue.replace(/^\//, "");
    return base ? `${base}/${sanitizedPath}` : sanitizedPath;
  }

  private reloadAudioSource(forceReset: boolean = false) {
    if (!this.audio) {
      return;
    }
    if (forceReset) {
      this.audio.pause();
      this.audio.currentTime = 0;
    }
    this.audio.src = this.normalizedUrl || "";
    if (this.normalizedUrl) {
      this.audio.load();
    }
  }
}
